/**
 * A very lightweight structural validator for our minimal validation needs in customer portal.
 * Infers the type of the validated data from the schema.
 * Supports string, number, boolean, array, object, string literal, and union.
 * Any of those can be marked optional.
 * See unit test for example schema.
 * See `test\types\simpleValidator.test-d.ts` for example of type inference.
 */

type PrimitiveTypeName = 'string' | 'number' | 'boolean'

const Optional = Symbol('Optional')
const OneOf = Symbol('OneOf')

/** Indicates that an object can't have extra properties not defined in the schema */
export const Exact = Symbol('Exact')

type Modifier = typeof Optional | typeof Exact

export type ValidationSchema =
  | PrimitiveTypeName
  | `${PrimitiveTypeName}?`
  | `"${string}"`
  | `"${string}"?`
  | readonly [ValidationSchema]
  | {
    type: typeof OneOf
    schemas: Array<ValidationSchema>
  }
  | {
    type: typeof Optional
    schema: ValidationSchema
  }
  | {
    [Exact]?: true
    [key: string]: ValidationSchema
  }

type OptionalSchema =
  | `${PrimitiveTypeName}?`
  | `"${string}"?`
  | {
    type: typeof Optional
    schema: ValidationSchema
  }

type RequiredSchemaKeys<S extends {}> = Exclude<{
  [Key in keyof S]: Key extends Modifier
    ? never
    : S extends Record<Key, OptionalSchema>
      ? never
      : Key
}[keyof S], undefined>

type OptionalSchemaKeys<S extends {}> = Exclude<{
  [Key in keyof S]: Key extends Modifier
    ? never
    : S extends Record<Key, OptionalSchema>
      ? Key
      : never
}[keyof S], undefined>

export type ValidatedType<Schema> =
  Schema extends `${infer P}?` ? ValidatedType<P> | undefined :
  Schema extends {
    type: typeof Optional
    schema: infer P
  } ? ValidatedType<P> | undefined :
  Schema extends 'string' ? string :
  Schema extends 'number' ? number :
  Schema extends 'boolean' ? boolean :
  Schema extends `"${infer S}"` ? S :
  Schema extends {
    type: typeof OneOf
    schemas: [...infer U]
  } ? ValidatedType<U[number]> :
  Schema extends readonly [infer S] ? Array<ValidatedType<S>> :
  Schema extends {} ? Utils.Simplify<{
    -readonly [key in RequiredSchemaKeys<Schema>]: ValidatedType<Schema[key]>
  } & {
    -readonly [key in OptionalSchemaKeys<Schema>]?: ValidatedType<Schema[key]>
  }> :
  unknown

export function optional<S extends ValidationSchema>(schema: S): {
  type: typeof Optional
  schema: S
} {
  return {
    type: Optional,
    schema,
  }
}

export function oneOf<S extends [...ValidationSchema[]]>(schemas: S): {
  type: typeof OneOf
  schemas: S
} {
  return {
    type: OneOf,
    schemas,
  }
}

function isArraySchema(schema: ValidationSchema): schema is readonly [ValidationSchema] {
  return Array.isArray(schema)
}

function isUnionSchema(schema: ValidationSchema): schema is {
  type: typeof OneOf,
  schemas: Array<ValidationSchema>,
} {
  return typeof schema === 'object' && 'type' in schema && schema.type === OneOf
}

function isOptionalSchema(schema: ValidationSchema): schema is OptionalSchema {
  return (typeof schema === 'string' && schema.substring(schema.length - 1) === '?') ||
    (typeof schema === 'object' && 'type' in schema && schema.type === Optional)
}

export function validate<S extends ValidationSchema>(schema: S, value: unknown): value is ValidatedType<S> {
  if (isOptionalSchema(schema)) {
    const isNullish = value === null || typeof value === 'undefined'
    if (isNullish) { return true }
    const innerSchema = typeof schema === 'string' ?
      schema.substring(0, schema.length - 1) as ValidationSchema :
      schema.schema
    return validate(innerSchema, value)
  }
  else if (typeof schema === 'string') {
    if (schema[0] === '"' && schema[schema.length - 1] === '"') {
      // String literal
      return value === schema.substring(1, schema.length - 1)
    } else {
      // Primitive type
      const type = typeof value
      return type === schema
    }
  }
  else if (isArraySchema(schema)) {
    if (!Array.isArray(value)) { return false }
    return value.every(member => validate(schema[0], member))
  }
  else if (isUnionSchema(schema)) {
    return schema.schemas.some(member => validate(member, value))
  }
  else if (typeof schema === 'object') {
    if (typeof value !== 'object' || value === null) { return false }

    const schemaKeys = Object.keys(schema)

    if (schema[Exact]) {
      const schemaKeySet = new Set(schemaKeys)
      if (Object.keys(value).some(key => !schemaKeySet.has(key))) { return false }
    }

    return schemaKeys.every(key => validate(schema[key], value[key]))
  }
  else {
    // Check that the above cases are exhaustive
    const _n: never = schema
    return false
  }
}
